import React, {useState, useEffect, useContext} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {UserContext} from '../../UserContext';
import {usePreventDoubleClick} from '../hooks/usePreventDoubleClick';
import {useHttpClient} from '../hooks/useHttpClient';
import {useNotificacoes} from '../hooks/useNotificacoes';

import Formulario from '../../components/ui/Formulario';
import BotaoSalvar from '../../components/BotaoSalvar';

import {isDataValida, formatarKwh10_2, mesesOptions} from '../../util/validacoes';

import styles from './CadastroConsumoCliente.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function CadastroConsumoCliente() {
    const {exibirErro} = useNotificacoes();
    const {id} = useParams();
    const isEdicao = Boolean(id);
    const {user} = useContext(UserContext);
    const navigate = useNavigate();
    const {get, post, put} = useHttpClient();

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const idEmpresa = userData?.empresa?.id || 0;

    const [formData, setFormData] = useState({
        idUsina: '',
        idCliente: '',
        idUnidadeConsumidora: '',
        mesRefBandeira: '',
        bandeira: '',
        dtVencimento: '',
        saldoAtualKwh: '',
        mesConsumo: '',
        dtInicioConsumo: new Date(),
        dtFimConsumo: new Date(),
        qtdConsumoKw: '',
        vlTarifa: '',
        mesInjecao: '',
        dtInicioInjecao: new Date(),
        dtFimInjecao: new Date(),
        qtdInjetadaKw: '',
        vlTaxaIluminacao: '',
        vlJurosMultaCredito: '',
        vlTusd: '',
        codigoBarras: '',
        linhaDigitavel: '',
        qtdCompensadoKw: '',
        unidadeNome: ''
    });

    const [usinas, setUsinas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [chaveAsaas, setChaveAsaas] = useState(true);

    useEffect(() => {
        if (user === null) navigate('/login');
    }, [user, navigate]);

    useEffect(() => {
        carregarUsinas();
        if (isEdicao) carregarConsumoCliente();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdicao]);

    useEffect(() => {
        if (formData.idUsina) {
            carregarClientes(formData.idUsina);
            verificarChaveAsaas(formData.idUsina);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.idUsina]);

    async function carregarUsinas() {
        try {
            const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${idEmpresa}`);
            const lista = Array.isArray(data) ? data : (data?.content ?? []);
            setUsinas(lista);
        } catch (err) {
            setUsinas([]);
            exibirErro('Erro ao buscar usinas.');
        }
    }

    async function carregarConsumoCliente() {
        try {
            const data = await get(`/hello-sol/consumos-cliente/${id}`);

            const idClienteSel = data?.unidadeConsumidora?.cliente?.id || '';
            const idUcSel = data?.unidadeConsumidora?.id ? String(data.unidadeConsumidora.id) : '';
            const idUsinaSel = data?.usina?.id || '';

            setFormData(prev => ({
                ...prev,
                ...data,
                idCliente: idClienteSel,
                idUsina: idUsinaSel,
                idUnidadeConsumidora: idUcSel,
                unidadeNome: data?.unidadeConsumidora?.nomeUnidade || '',
                dtVencimento: data?.dtVencimento
                    ? new Date(data.dtVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                    : '',
                saldoAtualKwh: formatarKwh10_2(String(data?.saldoAtualKwh ?? '')),
                qtdConsumoKw: formatarKwh10_2(String(data?.qtdConsumoKw ?? '')),
                qtdInjetadaKw: formatarKwh10_2(String(data?.qtdInjetadaKw ?? '')),
                qtdCompensadoKw: formatarKwh10_2(String(data?.qtdCompensadoKw ?? '')),
                vlTusd: formatValor(String(data?.vlTusd ?? '0'))
            }));

            await carregarClientes(idUsinaSel);
            await fetchUnidadesDoCliente(idClienteSel);
        } catch (err) {
            exibirErro(err.message || 'Erro ao carregar dados do consumo.');
        }
    }

    async function carregarClientes(idUsinaParam) {
        try {
            const idUsinaBusca = idUsinaParam || formData.idUsina;

            if (!idUsinaBusca) {
                setUsuarios([]);
                return [];
            }

            const data = await get(`/hello-sol/usuarios/buscarUsuarioUsina?idUsina=${idUsinaBusca}&idTipoUsuario=3`);
            const lista = Array.isArray(data) ? data : (data?.content ?? []);
            setUsuarios(lista);
            return lista;
        } catch (err) {
            setUsuarios([]);
            exibirErro(err.message || 'Erro ao buscar clientes.');
            return [];
        }
    }

    const fetchUnidadesDoCliente = async (idClienteSel) => {
        if (!idClienteSel) {
            setUnidades([]);
            return [];
        }
        const qs = new URLSearchParams({ idCliente: idClienteSel }).toString();
        const r = await get(`/hello-sol/unidades-consumidoras/buscar?${qs}`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUnidades(lista);
        return lista;
    };

    async function verificarChaveAsaas(idUsinaParam) {
        try {
            const idUsinaBusca = idUsinaParam || formData.idUsina;
            if (!idUsinaBusca) return;
            const result = await get(`/hello-sol/usinas/${idUsinaBusca}/chave-asaas`);
            setChaveAsaas(!!result);
        } catch (err) {
            exibirErro('Erro ao verificar chave Asaas.');
        }
    }

    const handleChange = async (e) => {
        const { name, value } = e.target;

        if (name === 'idUsina') {
            setUsuarios([]);
            setUnidades([]);
            setChaveAsaas(true);
            setFormData(prev => ({
                ...prev,
                idUsina: value,
                idCliente: '',
                idUnidadeConsumidora: '',
                unidadeNome: ''
            }));
            return;
        }

        if (name === 'idCliente') {
            setFormData(prev => ({
                ...prev,
                idCliente: value,
                idUnidadeConsumidora: '',
            }));
            await fetchUnidadesDoCliente(value);
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateInput = (e, fieldName) => {
        let value = e.target.value.replace(/\D/g, "");
        if (value.length > 2) value = value.replace(/^(\d{2})(\d)/, "$1/$2");
        if (value.length > 5) value = value.replace(/^(\d{2}\/\d{2})(\d+)/, "$1/$2");
        setFormData(prev => ({...prev, [fieldName]: value}));
    };

    const formatValor = (value) => {
        const numericValue = parseFloat(String(value).replace(',', '.').replace(/[^\d.]/g, ''));
        return isNaN(numericValue)
            ? '0,00000'
            : Math.min(numericValue, 9999999999.99999).toFixed(5).replace('.', ',');
    };

    const formatValorTarifa = (value) => {
        const numericValue = parseFloat(String(value).replace(',', '.').replace(/[^\d.]/g, ''));
        return isNaN(numericValue)
            ? '0,0000000'
            : Math.min(numericValue, 9999999999.9999999).toFixed(7).replace('.', ',');
    };

    const normalizarDecimalParaApi = (valor) => {
        if (valor === null || valor === undefined || valor === '') return null;
        return String(valor).replace(/\./g, '').replace(',', '.');
    };

    async function salvarConsumo() {
        if (!isDataValida(formData.dtVencimento)) {
            exibirErro('Data de vencimento inválida. Utilize o formato dd/mm/aaaa e uma data real.');
            return;
        }

        try {
            const payload = {
                ...formData,
                dtVencimento: formData.dtVencimento.toString().length === 10
                    ? formData.dtVencimento.split('/').reverse().join('-')
                    : formData.dtVencimento,
                vlTusd: normalizarDecimalParaApi(formData.vlTusd)
            };

            if (isEdicao) await put(`/hello-sol/consumos-cliente/${id}`, payload);
            else await post(`/hello-sol/consumos-cliente`, payload);

            navigate('/home/listar-cliente-consumo');
        } catch (error) {
            exibirErro(error.message || 'Erro ao salvar consumo.');
        }
    }

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarConsumo);

    return (
        <div className={styles_global.container}>
            <h2>{isEdicao ? 'Alterar Consumo' : 'Cadastrar Consumo'}</h2>

            <Formulario onSubmit={safeSubmit}>
                <div className={styles.linhaConsumoCliente3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina*</label>
                        <select
                            name="idUsina"
                            value={formData.idUsina}
                            onChange={handleChange}
                            className={styles.campoUsina}
                            required
                            disabled={isEdicao}
                        >
                            <option value="">Selecionar Usina</option>
                            {(usinas || []).map(usina => (
                                <option key={usina.id} value={usina.id}>{usina.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idCliente">Cliente*</label>
                        <select
                            name="idCliente"
                            value={formData.idCliente}
                            onChange={handleChange}
                            className={styles.campoCliente}
                            required
                            disabled={isEdicao}
                        >
                            <option value="">Selecionar Cliente</option>
                            {(usuarios || []).map(usuario => (
                                <option key={usuario.id} value={usuario.id}>{usuario.nome}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idUnidadeConsumidora">Unidade*</label>
                        <select
                            id="idUnidadeConsumidora"
                            name="idUnidadeConsumidora"
                            className={styles.campoUnidade}
                            value={formData.idUnidadeConsumidora}
                            onChange={handleChange}
                            required
                            disabled={isEdicao}
                        >
                            <option value="">Selecionar Unidade</option>
                            {(unidades || []).map((uc) => (
                                <option key={uc.id} value={String(uc.id)}>
                                    {uc.nomeUnidade}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente3}>
                    <div className={styles_content.formField}>
                        <label htmlFor="bandeira">Bandeira*</label>
                        <select
                            name="bandeira"
                            value={formData.bandeira}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecionar Bandeira</option>
                            <option value="AMARELA">Amarela</option>
                            <option value="ESCASSEZ HIDRICA">Escassez Hídrica</option>
                            <option value="VERDE">Verde</option>
                            <option value="VERMELHA">Vermelha</option>
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="dtVencimento">Data de Vencimento da Fatura do Cliente*</label>
                        <input
                            type="text"
                            name="dtVencimento"
                            className={styles.campoDtVencimento}
                            value={formData.dtVencimento || ''}
                            onChange={(e) => handleDateInput(e, 'dtVencimento')}
                            placeholder="dd/mm/aaaa"
                            maxLength={10}
                            required
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="saldoAtualKwh">Saldo Atual KWh</label>
                        <input
                            type="text"
                            name="saldoAtualKwh"
                            className={styles.camposaldoAtualKwh}
                            value={formData.saldoAtualKwh || ''}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    saldoAtualKwh: formatarKwh10_2(e.target.value)
                                }))
                            }
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="mesConsumo">Período de Consumo*</label>
                        <select
                            name="mesConsumo"
                            className={styles.campoPeriodoConsumo}
                            value={formData.mesConsumo}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecionar Mês</option>
                            {mesesOptions()}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="qtdConsumoKw">Quantidade KWh Consumido*</label>
                        <input
                            name="qtdConsumoKw"
                            className={styles.campoQtdConsumo}
                            value={formData.qtdConsumoKw}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    qtdConsumoKw: formatarKwh10_2(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="mesInjecao">Período de Injeção*</label>
                        <select
                            name="mesInjecao"
                            className={styles.campoPeriodoInjecao}
                            value={formData.mesInjecao}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Selecionar Mês</option>
                            {mesesOptions()}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="qtdInjetadaKw">Quantidade KWh Injetado*</label>
                        <input
                            name="qtdInjetadaKw"
                            className={styles.campoQtdInjetado}
                            value={formData.qtdInjetadaKw}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    qtdInjetadaKw: formatarKwh10_2(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="vlTarifa">Valor da Tarifa R$/KWh (com imposto)*</label>
                        <input
                            name="vlTarifa"
                            className={styles.campoVlTarifa}
                            value={formData.vlTarifa}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    vlTarifa: formatValorTarifa(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="qtdCompensadoKw">Quantidade KWh Compensado*</label>
                        <input
                            name="qtdCompensadoKw"
                            className={styles.campoQtdCompensado}
                            value={formData.qtdCompensadoKw}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    qtdCompensadoKw: formatarKwh10_2(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>
                </div>

                <div className={styles.linhaConsumoCliente}>
                    <div className={styles_content.formField}>
                        <label htmlFor="vlTaxaIluminacao">Valor da Taxa de Iluminação*</label>
                        <input
                            name="vlTaxaIluminacao"
                            className={styles.campoVlTarifa}
                            value={formData.vlTaxaIluminacao}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    vlTaxaIluminacao: formatValor(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="vlJurosMultaCredito">Valor de Juros e Multa*</label>
                        <input
                            name="vlJurosMultaCredito"
                            className={styles.campoVlJuros}
                            value={formData.vlJurosMultaCredito}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    vlJurosMultaCredito: formatValor(e.target.value)
                                }))
                            }
                            required
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="vlTusd">Desconto TUSD</label>
                        <input
                            type="text"
                            id="vlTusd"
                            name="vlTusd"
                            className={styles.campoVlTarifa}
                            value={formData.vlTusd || ''}
                            onChange={handleChange}
                            onBlur={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    vlTusd: formatValor(e.target.value)
                                }))
                            }
                            placeholder="0,00000"
                        />
                    </div>

                    {chaveAsaas && (
                        <div className={styles_content.formField}>
                            <label htmlFor="codigoBarras">Código de Barras:</label>
                            <input
                                type="text"
                                id="codigoBarras"
                                name="codigoBarras"
                                className={styles.campoCodBarras}
                                value={formData.codigoBarras}
                                onChange={handleChange}
                                required={chaveAsaas}
                            />
                        </div>
                    )}

                    {chaveAsaas && (
                        <div className={styles_content.formField}>
                            <label htmlFor="linhaDigitavel">Linha Digitavel:</label>
                            <input
                                type="text"
                                id="linhaDigitavel"
                                name="linhaDigitavel"
                                className={styles.campoCodBarras}
                                value={formData.linhaDigitavel}
                                onChange={handleChange}
                                required={chaveAsaas}
                            />
                        </div>
                    )}
                </div>

                <div className={styles_content.buttonGroup}>
                    <BotaoSalvar loading={isSubmitting} onClick={safeSubmit}/>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroConsumoCliente;